import { ApiError } from '~/api/errors';

import type { CheckIssue } from '../types/taxonomy-studio.types';

/**
 * Taxonomy Studio's single error model. Every non-2xx `/taxonomy/*` response is RFC 9457
 * problem JSON (`type, title, status, code, detail, field?, errors?, items?, conflicts?,
 * current?, request_id?`); network failures and timeouts have no body. `readProblem`
 * normalises all of them so every surface (toasts, inline form errors, panel error
 * states, the full-page access message) reads the same shape.
 */

/** What went wrong at the transport level. */
export type ProblemKind = 'http' | 'network' | 'timeout' | 'unknown';

export interface TaxonomyProblem {
  kind: ProblemKind;
  status: number | null;
  code: string | undefined;
  /** Human-readable explanation from the server (or a transport fallback). */
  detail: string;
  /** First failing field (422). */
  field: string | undefined;
  /** Per-field messages: `errors[]` plus `field` → `detail` when no list was sent. */
  fieldErrors: Record<string, string>;
  /** 412 STALE_ITEM: the item as it is now. */
  current: unknown;
  /** 409 UNDO_CONFLICT: items edited since the action. */
  conflicts: { entityType: string; entityKey: string; entityLabel: string }[];
  /** Per-item failures on bulk calls (BATCH_INVALID). */
  items: { key: string; code: string; detail: string }[];
  /** 500 INTERNAL: id to quote to support. */
  requestId: string | undefined;
}

/** Codes that mean "you may not use the studio" — shown as a full-page message. */
export const ACCESS_CODES = new Set([
  'NOT_PLATFORM_ADMIN',
  'INSUFFICIENT_SCOPE',
  'UNAUTHENTICATED',
  'INVALID_USER_TOKEN',
]);

/** Codes whose request is safe to simply send again. */
const RETRYABLE_CODES = new Set(['CONFLICT', 'INTERNAL']);

interface ProblemBody {
  code?: unknown;
  detail?: unknown;
  title?: unknown;
  field?: unknown;
  errors?: unknown;
  current?: unknown;
  conflicts?: unknown;
  items?: unknown;
  request_id?: unknown;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Transport failures (no response body) by the error ky / fetch throws. */
function transportKind(err: unknown): ProblemKind {
  if (err instanceof Error) {
    if (err.name === 'TimeoutError' || err.name === 'AbortError') return 'timeout';
    if (err.name === 'TypeError') return 'network';
  }
  return 'unknown';
}

/**
 * Reads the problem JSON off a failed taxonomy call. The shared error interceptor already
 * parsed the body into `ApiError.body`; this only picks members out, never leaking raw
 * JSON or `[object Object]` into the UI.
 */
export function readProblem(err: unknown): TaxonomyProblem {
  if (!(err instanceof ApiError)) {
    const kind = transportKind(err);
    return {
      kind,
      status: null,
      code: undefined,
      detail: '',
      field: undefined,
      fieldErrors: {},
      current: undefined,
      conflicts: [],
      items: [],
      requestId: undefined,
    };
  }
  const body: ProblemBody = isRecord(err.body) ? (err.body as ProblemBody) : {};
  const fieldErrors: Record<string, string> = {};
  if (Array.isArray(body.errors)) {
    for (const e of body.errors.filter(isRecord)) {
      const field = str(e.field);
      const message = str(e.message);
      if (field && message && !(field in fieldErrors)) fieldErrors[field] = message;
    }
  }
  const field = str(body.field) || undefined;
  const detail = str(body.detail) || str(body.title) || '';
  if (field && !(field in fieldErrors) && detail) fieldErrors[field] = detail;
  return {
    kind: 'http',
    status: err.statusCode,
    code: str(body.code) || err.serverCode,
    detail,
    field,
    fieldErrors,
    current: body.current,
    conflicts: Array.isArray(body.conflicts)
      ? body.conflicts.filter(isRecord).map((c) => ({
          entityType: str(c.entity_type),
          entityKey: str(c.entity_key),
          entityLabel: str(c.entity_label),
        }))
      : [],
    items: Array.isArray(body.items)
      ? body.items.filter(isRecord).map((i) => ({
          key: str(i.key),
          code: str(i.code),
          detail: str(i.detail),
        }))
      : [],
    requestId: str(body.request_id) !== '' ? str(body.request_id) : (err.reference ?? undefined),
  };
}

/** True for 401s and the access 403s — the studio cannot be used at all. */
export function isAccessProblem(p: TaxonomyProblem): boolean {
  return p.status === 401 || (p.code !== undefined && ACCESS_CODES.has(p.code));
}

/** True when sending the same request again may succeed. */
export function isRetryable(p: TaxonomyProblem): boolean {
  return (
    p.kind === 'network' ||
    p.kind === 'timeout' ||
    (p.status !== null && p.status >= 500) ||
    (p.code !== undefined && RETRYABLE_CODES.has(p.code))
  );
}

/** True when a form should keep itself open and show the problem inline. */
export function isFormProblem(p: TaxonomyProblem): boolean {
  return p.kind === 'http' && p.status !== null && (p.status === 422 || p.status === 409);
}

/** i18n key of the friendly title for a problem (code first, then status / transport). */
export function problemTitleKey(p: TaxonomyProblem): string {
  if (p.kind === 'network') return 'errors.title.NETWORK';
  if (p.kind === 'timeout') return 'errors.title.TIMEOUT';
  if (p.code) return `errors.title.${p.code}`;
  if (p.status !== null && p.status >= 500) return 'errors.title.INTERNAL';
  if (p.status === 404) return 'errors.title.NOT_FOUND';
  return 'errors.title.UNKNOWN';
}

/** i18n key of the default explanation, used when the server sent no `detail`. */
export function problemFallbackKey(p: TaxonomyProblem): string {
  if (p.kind === 'network') return 'errors.detail.NETWORK';
  if (p.kind === 'timeout') return 'errors.detail.TIMEOUT';
  if (p.status !== null && p.status >= 500) return 'errors.detail.INTERNAL';
  return 'errors.detail.UNKNOWN';
}

/**
 * 409 CHECKS_FAILED carries the failing publish checks in `errors[]` (same shape as
 * `GET /draft/checks`). Read them so the popover shows exactly what blocked the publish,
 * without waiting for a refetch.
 */
export function readCheckErrors(err: unknown): CheckIssue[] {
  if (!(err instanceof ApiError) || !isRecord(err.body)) return [];
  const errors = (err.body as ProblemBody).errors;
  if (!Array.isArray(errors)) return [];
  return errors.filter(isRecord).flatMap((e) => {
    const code = str(e.code);
    const message = str(e.message);
    if (!code || !message) return [];
    const target = isRecord(e.target) ? { type: str(e.target.type), key: str(e.target.key) } : null;
    return [{ code, message, target, count: typeof e.count === 'number' ? e.count : null }];
  });
}
